import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AttachMoney as CapExIcon,
  Build as OpExIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import CapExEditor from '../../components/CapExEditor';
import OpExEditor from '../../components/OpExEditor';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors } from '@vortx/shared-ui';

const CapExEditorPage = () => {
  const { userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = userProfile?.is_super_admin;

  // URL-synced tab state for browser back/refresh support
  const costType = searchParams.get('costType') || 'capex';
  const setCostType = (value) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('costType', value);
      return newParams;
    }, { replace: true });
  };

  // Scope: 'company' or 'global' - company admins only see company
  const scope = searchParams.get('scope') || 'company';
  const setScope = (value) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('scope', value);
      return newParams;
    }, { replace: true });
  };

  return (
    <AdminLayout title="CAPEX & OPEX">
      {/* Cost Type Tabs (CAPEX vs OPEX) */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={costType}
          onChange={(e, newValue) => setCostType(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: vortxColors.stone,
              minHeight: 48,
              '&.Mui-selected': { color: vortxColors.white }
            },
            '& .MuiTabs-indicator': { bgcolor: vortxColors.gold }
          }}
        >
          <Tab
            value="capex"
            icon={<CapExIcon />}
            iconPosition="start"
            label="Capital Costs (CAPEX)"
            sx={{
              bgcolor: costType === 'capex' ? alpha(vortxColors.gold, 0.15) : 'transparent',
              borderRadius: '8px 8px 0 0',
              mr: 0.5
            }}
          />
          <Tab
            value="opex"
            icon={<OpExIcon />}
            iconPosition="start"
            label="Operating Costs (OPEX)"
            sx={{
              bgcolor: costType === 'opex' ? alpha(vortxColors.teal, 0.15) : 'transparent',
              borderRadius: '8px 8px 0 0'
            }}
          />
        </Tabs>
      </Box>

      {/* Scope Toggle - Only for Super Admins */}
      {isSuperAdmin && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: vortxColors.stone, mb: 1, display: 'block' }}>
            Editing Scope:
          </Typography>
          <Tabs
            value={scope}
            onChange={(e, newValue) => setScope(newValue)}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                color: vortxColors.stone,
                minHeight: 36,
                py: 0.5,
                px: 2,
                fontSize: '0.8rem',
                '&.Mui-selected': {
                  color: costType === 'capex' ? vortxColors.gold : vortxColors.teal
                }
              },
              '& .MuiTabs-indicator': {
                bgcolor: costType === 'capex' ? vortxColors.gold : vortxColors.teal
              }
            }}
          >
            <Tab
              value="company"
              label="Company"
              sx={{
                bgcolor: scope === 'company'
                  ? alpha(costType === 'capex' ? vortxColors.gold : vortxColors.teal, 0.1)
                  : 'transparent',
                borderRadius: '4px 4px 0 0'
              }}
            />
            <Tab
              value="global"
              label="Global Library"
              sx={{
                bgcolor: scope === 'global'
                  ? alpha(costType === 'capex' ? vortxColors.gold : vortxColors.teal, 0.1)
                  : 'transparent',
                borderRadius: '4px 4px 0 0'
              }}
            />
          </Tabs>
        </Box>
      )}

      {/* Editor based on cost type */}
      {costType === 'capex' ? (
        <CapExEditor scope={isSuperAdmin ? scope : 'company'} />
      ) : (
        <OpExEditor scope={isSuperAdmin ? scope : 'company'} />
      )}
    </AdminLayout>
  );
};

export default CapExEditorPage;
